/**
 * 聊天消息管理 Composable（主入口）
 * 整合所有消息相关功能：发送、编辑、删除、历史加载等
 */

import { ref, computed, nextTick, watch } from "vue";
import { useChat } from "@ai-sdk/vue";
import { ChatRole, AIModel, ChatToolMode } from "~/enums";
import { useChatSession } from "./useChatSession";
import { useThinkingProcess } from "./useThinkingProcess";
import { useAnonymousUser } from "./useAnonymousUser";
import { useMessagePersistence } from "./useMessagePersistence";
import { useMessageFeedback } from "./useMessageFeedback";
import { useMessageSave } from "./useMessageSave";
import { useMessageHistory } from "./useMessageHistory";
import { useMessageOperations } from "./useMessageOperations";
import { useMemorySettings } from "./useMemorySettings";
import { useSessionMessageStore } from "./useSessionMessageStore";
import { ensureChatMessageTimes } from "~/utils";
import { normalizeChatErrorMessage } from "~/utils/chat-error";

export function useChatMessages() {
  // 依赖的 composables
  const { sessionId, setSessionId, initSession, updateSessionTitle } =
    useChatSession();
  const { anonymousId, getAnonymousId } = useAnonymousUser();
  const { isMemoryEnabled } = useMemorySettings();
  const sessionMessageStore = useSessionMessageStore();
  const { thinkingSteps, clearThinkingSteps, setThinkingSteps } =
    useThinkingProcess();
  const feedbackManager = useMessageFeedback();

  // 当前用户 ID - 使用 getOrCreateAnonymousId() 确保始终获取正确的 ID
  const currentUserId = computed(() => getAnonymousId());

  const initialMessages = ref<any[]>([]); // 存储初始消息
  const activeMessagesSessionId = ref("");
  let isHydratingMessages = false;

  const syncCurrentSessionCache = () => {
    // 空列表不写入缓存，避免路由切换或 SDK 短暂清空时覆盖已有会话历史。
    if (messages.value.length === 0) {
      return;
    }

    sessionMessageStore.setMessages(
      activeMessagesSessionId.value,
      messages.value,
    );
  };

  const clearCurrentSessionCache = () => {
    sessionMessageStore.clearMessages(
      activeMessagesSessionId.value || sessionId.value,
    );
  };

  const setActiveMessagesSession = (targetSessionId: string) => {
    activeMessagesSessionId.value = targetSessionId;
  };

  // ==================== 初始化子模块（必须在 useChat 之前）====================

  // 1. 消息保存模块
  const { saveMessage, loadAndConvertMsg } = useMessageSave();

  /**
   * 设置消息完成回调
   */
  let onMessageFinished: (() => void) | null = null;
  const setOnMessageFinished = (callback: () => void) => {
    onMessageFinished = callback;
  };
  let onSessionPersisted: (() => void) | null = null;
  const setOnSessionPersisted = (callback: () => void) => {
    onSessionPersisted = callback;
  };

  const isLoading = computed(() => status.value === "streaming"); // 加载状态

  // 存储待发送的附件数据
  const pendingAttachments = ref<any[]>([]);
  const pendingClientTraceId = ref("");
  const latestClientTraceId = ref("");

  // 存储用户选择的模型；auto 表示由后端根据附件类型自动路由
  const selectedModel = ref<AIModel | "auto">("auto");
  const selectedPromptTemplateKey = ref<string | null>(null);

  // 从Vercel AI SDK - useChat 中获取 messages
  const { messages, input, handleSubmit, status, error, stop, reload } =
    useChat({
      initialMessages: initialMessages.value,
      api: "/api/chat/chat",
      // 使用 experimental_prepareRequestBody 动态构造请求体（v4 推荐做法）
      experimental_prepareRequestBody: ({ id, messages: sdkMessages }) => {
        const requestUserId = currentUserId.value || getAnonymousId();
        const body: any = {
          messages: sdkMessages,
          sessionId: sessionId.value,
          userId: requestUserId,
          memoryEnabled: isMemoryEnabled(),
          attachments: pendingAttachments.value,
          promptTemplateKey: selectedPromptTemplateKey.value,
          clientTraceId: pendingClientTraceId.value,
          toolMode: ChatToolMode.AUTO,
          confirmedTools: [],
        };
        if (selectedModel.value !== "auto") {
          body.model = selectedModel.value; // 仅在用户手动选择模型时传递
        }
        console.log(
          `📤 experimental_prepareRequestBody 被调用, attachments数量: ${pendingAttachments.value?.length || 0}, model: ${selectedModel.value}`,
        );
        return body;
      },
      onError: (err) => {
        console.warn("AI 回复失败，已作为助手消息展示:", normalizeChatErrorMessage(err));
        persistAssistantErrorMessage(err);
      },
      onResponse: (response) => {
        const responseTraceId = response.headers.get("X-Trace-ID");
        if (responseTraceId) {
          pendingClientTraceId.value = responseTraceId;
          latestClientTraceId.value = responseTraceId;
        }
      },
      onFinish: async (message, { usage, finishReason }) => {
        // console.log("✅ AI 回复完成:", message, usage, finishReason);

        let sortIndex = messages.value.length - 1;
        const { role, content } = message;
        const currentTraceId = pendingClientTraceId.value || latestClientTraceId.value;

        // 后端缓存获取思考步骤（使用 sessionId 作为 key）
        let thinkingStepsData: any[] = [];
        if (sessionId.value) {
          try {
            // 🔗调用接口：获取思考步骤数据 GET /api/steps/:sessionId
            const stepsResponse: any = await $fetch(
              `/api/steps/${sessionId.value}`,
              {
                method: "GET",
              },
            ).catch(() => null);

            if (stepsResponse && stepsResponse.success && stepsResponse.steps) {
              thinkingStepsData = stepsResponse.steps;
              console.log(
                `📊 从缓存获取思考步骤: ${thinkingStepsData.length}步`,
              );
            } else {
              console.log("⚠️ 未找到缓存的思考步骤数据");
            }
          } catch (error) {
            console.warn("⚠️ 获取思考步骤失败:", error);
          }
        }

        // 保存消息到数据库（包含 thinkingSteps）
        const savedMessage = await saveMessage(
          sessionId.value,
          currentUserId.value,
          role,
          content,
          sortIndex,
          thinkingStepsData.length > 0 ? thinkingStepsData : undefined,
        );

        if (savedMessage && savedMessage.id) {
          const targetMessage = messages.value[sortIndex];
          if (targetMessage) {
            (targetMessage as any).id = savedMessage.id;
            if (currentTraceId) {
              (targetMessage as any).traceId = currentTraceId;
            }

            // 附加思考步骤到消息对象
            if (thinkingStepsData.length > 0) {
              (targetMessage as any).thinkingSteps = thinkingStepsData;
              // 同时更新全局思考步骤状态用于 Drawer 展示
              setThinkingSteps(thinkingStepsData);
              console.log(`📊 加载思考步骤: ${thinkingStepsData.length}步`);
            }

            console.log(
              `🔄 消息已关联数据库 ID: SDK=${message.id}, DB=${savedMessage.id}`,
            );
          }
        }

        syncCurrentSessionCache();
        pendingClientTraceId.value = "";

        if (role === ChatRole.ASSISTANT && sortIndex === 1 && savedMessage?.id && sessionId.value) {
          const titleGenerated = await requestGeneratedSessionTitle(sessionId.value);
          if (titleGenerated) {
            onSessionPersisted?.();
          }
        }

        // 通知外部刷新会话列表
        onMessageFinished?.();
      },
    });

  const persistAssistantErrorMessage = async (err: unknown) => {
    const content = normalizeChatErrorMessage(err);
    const lastMessage = messages.value[messages.value.length - 1];

    if (lastMessage?.role === ChatRole.ASSISTANT && lastMessage.content === content) {
      return;
    }

    const traceId = pendingClientTraceId.value || latestClientTraceId.value;
    const assistantMessage: any = {
      id: `error_${Date.now().toString(36)}`,
      role: ChatRole.ASSISTANT,
      content,
      parts: [{ type: "text", text: content }],
      createdAt: new Date(),
      traceId,
      isError: true,
    };

    messages.value.push(assistantMessage);
    syncCurrentSessionCache();

    if (!sessionId.value || !currentUserId.value) {
      return;
    }

    const savedMessage = await saveMessage(
      sessionId.value,
      currentUserId.value,
      ChatRole.ASSISTANT,
      content,
      messages.value.length - 1,
    );

    if (savedMessage?.id) {
      assistantMessage.id = savedMessage.id;
      syncCurrentSessionCache();
    }

    pendingClientTraceId.value = "";
    onMessageFinished?.();
  };

  /**
   * 请求生成会话标题
   */
  const requestGeneratedSessionTitle = async (targetSessionId: string) => {
    try {
      const response: any = await $fetch("/api/session/generate-title", {
        method: "POST",
        body: {
          sessionId: targetSessionId,
          userId: currentUserId.value,
        },
      });
      return response?.success === true && response?.skipped !== true;
    } catch (titleError) {
      console.warn("Session title generation failed:", titleError);
      return false;
    }
  };

  // 消息持久化应该基于 initialMessages，而不是 messages
  const persistence = useMessagePersistence(initialMessages);

  // 监听 initialMessages 变化，同步到 useChat 的 messages
  watch(
    initialMessages,
    (newMessages) => {
      if (newMessages && newMessages.length > 0) {
        messages.value = [...newMessages];
      }
    },
    { immediate: true },
  );

  watch(
    messages,
    () => {
      ensureChatMessageTimes(messages.value);
      syncCurrentSessionCache();
    },
    { deep: true, immediate: true },
  );

  // ==================== 初始化其他子模块（在 useChat 之后）====================

  // 2. 历史消息管理模块
  const historyManager = useMessageHistory(
    initialMessages,
    messages,
    currentUserId,
    {
      get: sessionMessageStore.getMessages,
      set: sessionMessageStore.setMessages,
      clear: sessionMessageStore.clearMessages,
    },
  );

  // 3. 消息操作模块
  const operationsManager = useMessageOperations(
    sessionId,
    currentUserId,
    messages,
    input,
    handleSubmit,
    reload,
    stop,
    status,
    initSession,
    updateSessionTitle,
    clearThinkingSteps,
    pendingAttachments, // 传递附件数据
    selectedModel, // 传递模型选择
    selectedPromptTemplateKey,
    pendingClientTraceId,
    {
      sync: syncCurrentSessionCache,
      clear: clearCurrentSessionCache,
      setActiveSession: setActiveMessagesSession,
      notifySessionPersisted: () => onSessionPersisted?.(),
    },
  );

  let isRestoringCurrentSession = false;
  const restoreCurrentSessionMessages = async () => {
    if (
      isRestoringCurrentSession ||
      messages.value.length > 0 ||
      !sessionId.value
    ) {
      return;
    }

    const userId = currentUserId.value || getAnonymousId();
    if (!userId) {
      return;
    }

    isRestoringCurrentSession = true;
    try {
      const cachedMessages = sessionMessageStore.getMessages(sessionId.value);
      if (cachedMessages.length > 0) {
        // 优先使用内存缓存，避免返回页面时再请求接口造成可见闪烁。
        setActiveMessagesSession(sessionId.value);
        isHydratingMessages = true;
        await historyManager.updateMessagesState(
          cachedMessages,
          sessionId.value,
          {
            loadFeedback: false,
          },
        );
        isHydratingMessages = false;
        return;
      }

      const restoredMessages = await loadAndConvertMsg(sessionId.value, userId);
      if (restoredMessages && restoredMessages.length > 0) {
        setActiveMessagesSession(sessionId.value);
        isHydratingMessages = true;
        await historyManager.updateMessagesState(
          restoredMessages,
          sessionId.value,
        );
        isHydratingMessages = false;
      }
    } catch (error) {
      console.error("❌ 恢复当前会话消息失败:", error);
    } finally {
      isHydratingMessages = false;
      isRestoringCurrentSession = false;
    }
  };

  // ==================== 导出所有功能 ====================

  watch(
    () => messages.value.length,
    async (messageCount, previousMessageCount) => {
      if (
        messageCount !== 0 ||
        previousMessageCount === 0 ||
        isHydratingMessages ||
        status.value === "streaming" ||
        !sessionId.value
      ) {
        return;
      }

      // useChat 在某些恢复/切换时会短暂清空 messages，这里兜底把当前会话补回来。
      await nextTick();
      await restoreCurrentSessionMessages();
    },
  );

  return {
    // 核心状态
    messages,
    input,
    isLoading,
    isLoadingHistory: historyManager.isLoadingHistory,
    error,
    currentUserId,

    // 编辑状态
    editingMessageIndex: operationsManager.editingMessageIndex,
    editingContent: operationsManager.editingContent,

    // 消息操作
    handleSend: operationsManager.handleSend,
    stopGeneration: operationsManager.stopGeneration,
    handleNewChat: operationsManager.handleNewChat,
    handleRegenerate: operationsManager.handleRegenerate,
    editUserMsg: operationsManager.editUserMsg,
    cancelEditUserMsg: operationsManager.cancelEditUserMsg,
    submitEditUserMsg: operationsManager.submitEditUserMsg,
    deleteUserMsg: operationsManager.deleteUserMsg,

    // ✅ 反馈相关（从 feedbackManager 导出）
    toggleLike: feedbackManager.toggleLike,
    toggleDislike: feedbackManager.toggleDislike,
    removeFeedback: feedbackManager.removeFeedback,
    loadMessageFeedback: feedbackManager.loadMessageFeedback,
    DISLIKE_REASONS: feedbackManager.DISLIKE_REASONS,

    // 历史消息管理
    loadHistoryMessages: historyManager.loadHistoryMessages,
    restoreCurrentSessionMessages,
    clearAllMessages: () => historyManager.clearAllMessages(sessionId.value),
    clearLocalMessages: () => historyManager.clearAllMessages(),
    setActiveMessagesSession,

    // 工具方法
    saveMessagesToStorage: () => persistence.saveMessages(),
    setOnMessageFinished,
    setOnSessionPersisted,

    // 附件管理
    pendingAttachments,

    // 模型选择
    selectedModel,
    selectedPromptTemplateKey,
    latestClientTraceId,
  };
}
